import { useEffect, useState } from "react";
import { ShieldAlert, FileSignature, Loader2, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, openSigned, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { SimpleMarkdown } from "@/components/SimpleMarkdown";
import { nok, formatDate } from "@/lib/format";

// Banner + signeringsdialog for selskapets lisensavtale. Vises når lisensen ikke er signert.
export function LicenseGate({ companyId, onSigned }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/companies/${companyId}/license`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  // Auto-åpne avtalen første gang et usignert selskap åpnes.
  useEffect(() => {
    setOpen(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const openDialog = () => {
    setOpen(true);
    if (!data) load();
  };

  const sign = async () => {
    setSigning(true);
    try {
      await api.post(`/companies/${companyId}/license/sign`);
      toast.success("Lisensavtalen er signert. Selskapet kan nå tas i bruk.");
      setOpen(false);
      onSigned?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const cfg = data?.config || {};
  const price = cfg.price_nok ?? 1990;
  const canSign = !!data?.can_sign;

  return (
    <>
      <div
        data-testid="license-gate-banner"
        className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
      >
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-amber-500/20 text-amber-600 dark:text-amber-400">
          <ShieldAlert className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-headings text-sm font-bold tracking-tight text-amber-900 dark:text-amber-200">
            Selskapet er ikke tatt i bruk ennå
          </p>
          <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-200/80">
            En lisensavtale ({nok(price)}/år) må signeres av styreleder, nestleder eller daglig leder før dere kan opprette møter, endre aksjer og laste opp dokumenter.
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2 bg-amber-600 text-white hover:bg-amber-600/90"
          data-testid="license-gate-open-btn"
          onClick={openDialog}
        >
          <FileSignature className="h-4 w-4" /> Vis og signer lisensavtale
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl gap-0 overflow-hidden p-0" data-testid="license-agreement-dialog">
          <DialogHeader className="border-b border-border/60 px-6 py-4">
            <DialogTitle className="flex items-center gap-2 font-headings">
              <FileSignature className="h-5 w-5 text-primary" /> Lisensavtale – Murphy Styrerom
            </DialogTitle>
            <DialogDescription>
              {cfg.issuer_name || "Murphy AS"}{cfg.issuer_org_number ? ` · org.nr. ${cfg.issuer_org_number}` : ""} · {nok(price)} per selskap per år
            </DialogDescription>
          </DialogHeader>

          {loading || !data ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <ScrollArea className="max-h-[52vh] px-6 py-4">
              <SimpleMarkdown text={cfg.agreement_terms} data-testid="license-terms" />
              <div className="mt-4 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                <div className="flex justify-between gap-4"><span>Avtalenummer</span><span className="font-medium text-foreground">{data.license?.invoice_number || "—"}</span></div>
                <div className="mt-1 flex justify-between gap-4"><span>Fornyelsesdato</span><span className="font-medium text-foreground">{formatDate(data.license?.renewal_date)}</span></div>
                <div className="mt-1 flex justify-between gap-4"><span>Årlig pris</span><span className="font-medium text-foreground">{nok(price)} eks. mva.</span></div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="flex-col items-stretch gap-3 border-t border-border/60 px-6 py-4 sm:flex-col sm:items-stretch">
            {data?.license?.signed ? (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-400" data-testid="license-already-signed">
                <CheckCircle2 className="h-4 w-4" /> Signert av {data.license.signed_by_name}
              </div>
            ) : canSign ? (
              <label className="flex cursor-pointer items-start gap-2.5 text-sm" data-testid="license-confirm-label">
                <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="license-confirm-check" className="mt-0.5" />
                <span className="text-muted-foreground">Jeg bekrefter at jeg har fullmakt til å inngå denne avtalen på vegne av selskapet, og godtar vilkårene.</span>
              </label>
            ) : (
              <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground" data-testid="license-cannot-sign">
                Kun styreleder, nestleder eller daglig leder kan signere denne avtalen.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="license-download-agreement"
                onClick={() => openSigned(`/companies/${companyId}/license/agreement-link`).catch(() => toast.error("Kunne ikke åpne avtalen"))}
              >
                <Download className="h-4 w-4" /> Last ned avtale (PDF)
              </Button>
              {!data?.license?.signed && canSign ? (
                <Button size="sm" className="gap-2" data-testid="license-sign-btn" disabled={!confirmed || signing} onClick={sign}>
                  {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Signer lisensavtale
                </Button>
              ) : null}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
